import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { InlineChart } from '@bingads-webui-campaign-react/inline-chart';
import { waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import appConfig from '@bingads-webui-component-legacy/config/index';
import Constants from '@bingads-webui-static-legacy/constants';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';

const AssetLibraryInlineChartWrapperInner = ({
  loadPerfTimeSeriesData,
  isShowChart,
  dateFilter,
  perfMarker,
  serverContext,
  instrumentation,
  chartPreference,
  i18nInlineChart,
  filters,
  appState,
  pageContext,
  forceToResizeChart,
}) => {
  const { palette } = ThemeFluent;

  const resizeWidth = useCallback(() => {
    const chartContainer = document.getElementsByClassName('asset-library-performance-inline-chart');

    if (chartContainer.length > 0) {
      const chartWidth = chartContainer[0].offsetWidth;

      return chartWidth;
    }
    return 0;
  }, []);

  const resizeChart = useRef();

  const [dateRange, setDateRange] = useState({});

  const onWindowResize = useCallback(_.debounce(() => {
    const resizeChartHandler = resizeChart.current;
    resizeChartHandler(resizeWidth());
  }, 50), []);

  const resizeChartListener = useCallback((handler) => {
    resizeChart.current = handler;
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize, false);

    onWindowResize();

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [onWindowResize]);

  const getDataSource = useCallback(() => {
    const dataSourceInner = {
      findAll: timeSeriesParams => loadPerfTimeSeriesData(timeSeriesParams, filters),
    };

    return dataSourceInner;
  }, [filters, loadPerfTimeSeriesData]);

  const getOptions = useCallback(() => {
    const optionsInner = {
      entityType: 'Asset',
      token: appConfig.get('odata.token'),
      imagePath: Constants.imagePath, // can be removed?
      instrumentation,
      pageContext,
      pageState: appState,
      preference: chartPreference,
      hideChartPanel: !isShowChart,
      customMetricNameList: ['Clicks', 'ClickThruRate', 'Impressions', 'Spent'],
      seriesColors: {
        primary: {
          original: palette.themePrimary,
          comparative: palette.themePrimary,
        },
        secondary: {
          original: '#022F22',
          comparative: '#022F22',
        },
      },
      lowestSupportedGranularity: 'Day',
      disableMovingAverage: true,
      addDateChangeListener: () => { },
      resizeChartListener,
      resizeEventHandler: () => { },
    };

    return optionsInner;
  }, [appState, chartPreference, instrumentation, isShowChart, pageContext, palette.themePrimary, resizeChartListener]);

  const dataSource = useMemo(() => getDataSource(), [getDataSource]);

  const options = useMemo(() => getOptions(), [getOptions]);

  const getNewDateRange = useCallback(() => {
    const newDateRange = waterfallDateFilterProcessor(
      [
        {
          filter: dateFilter,
        },
      ],
      serverContext
    );

    return newDateRange;
  }, [dateFilter, serverContext]);

  useEffect(() => {
    const newDateRange = getNewDateRange();
    if (!_.isEqual(dateRange, newDateRange)) {
      setDateRange(newDateRange);
    }
  }, [dateFilter, dateRange, getNewDateRange]);

  useEffect(() => {
    const resizeChartHandler = resizeChart.current;
    if (resizeChartHandler && isShowChart && forceToResizeChart) {
      const newWidth = resizeWidth();
      if (newWidth > 0) {
        resizeChartHandler(newWidth);
      }
    }
  }, [resizeWidth, isShowChart, forceToResizeChart]);

  return (
    <div className="asset-library-inline-chart-wrapper chart-area-wrapper">
      <InlineChart
        isShowChart={isShowChart}
        i18n={i18nInlineChart}
        dataSource={dataSource}
        dateRange={dateRange}
        perfMarker={perfMarker}
        serverContext={serverContext}
        options={options}
        customFilter={filters}
      />
    </div>
  );
};

AssetLibraryInlineChartWrapperInner.propTypes = {
  isShowChart: PropTypes.bool.isRequired,
  loadPerfTimeSeriesData: PropTypes.func.isRequired,
  dateFilter: PropTypes.shape({}),
  perfMarker: PropTypes.shape({}),
  serverContext: PropTypes.shape({
    DynamicPermissions: PropTypes.shape({
      InlineChartColumnMerticsEnabled: PropTypes.bool,
      CampaignsCombineGridAndChartOdataCallEnabled: PropTypes.bool,
    }),
  }),
  instrumentation: PropTypes.shape({}),
  chartPreference: PropTypes.shape({}),
  pageContext: PropTypes.shape({}),
  i18nInlineChart: PropTypes.shape({}).isRequired,
  filters: PropTypes.shape({}),
  appState: PropTypes.shape({}),
  forceToResizeChart: PropTypes.string,
};

AssetLibraryInlineChartWrapperInner.defaultProps = {
  dateFilter: null,
  perfMarker: null,
  serverContext: null,
  instrumentation: null,
  chartPreference: undefined,
  pageContext: null,
  filters: {},
  appState: null,
  forceToResizeChart: null,
};

export const AssetLibraryInlineChartWrapper = withInstrumentation(AssetLibraryInlineChartWrapperInner);

